import type { ButtonHTMLAttributes, Ref } from "react";
import { Spinner } from "../Spinner/Spinner";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost";

/** Class names for button-styled anchors (e.g. error pages, which must not depend on the router). */
export function buttonClassName(variant: ButtonVariant = "primary", extra?: string): string {
  return [styles.button, styles[variant], extra].filter(Boolean).join(" ");
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shows a spinner, blocks repeat clicks, and announces the busy state. */
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Button (WEB-006). Defaults to type="button": the HTML default is "submit", which lets a stray
 * button inside a form submit clinical data unintentionally. Submit buttons must say so.
 */
export function Button({
  variant = "primary",
  loading = false,
  type = "button",
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={buttonClassName(variant, className)}
    >
      {loading && <Spinner size="small" decorative />}
      {children}
    </button>
  );
}
